<template>
  <div
    ref="converterEl"
    data-cy="converter"
    class="converter"
    :class="{
      condensed,
      'converter-stepper': showStepper,
      'horizontal-input': forceHorizontal
    }"
  >
    <div
      v-if="mode === 'write'"
      class="common write-mode"
      data-cy="write-mode"
      :class="[
        severityClass,
        { ratio: isRatio },
        { disabled: props.disabled },
        { hidden: props.hidden }
      ]"
    >
      <span :class="severityClass" data-cy="write-label">
        <icon
          v-if="!disabled && errors.length > 0 && !props.disableLabel"
          :icon="['fas', 'exclamation-triangle']"
          class="mr-1"
          size="sm"
          data-cy="write-icon"
        />
        {{ label }}</span
      >
      <span
        class="p-inputnumber p-component p-inputwrapper input p-inputwrapper-filled"
      >
        <InputText
          :id="key.toString()"
          v-tooltip.focus.bottom="tooltipData"
          class="input"
          :placeholder="placeholder"
          :model-value="
            modelValue === null
              ? ''
              : isFocused
                ? pendingValue
                : localizedValueWithCommas
          "
          :disabled="disabled"
          @focusin="onFocusIn"
          @focusout="onFocusOut"
          @keydown="onKeydown"
          @update:model-value="pendingValue = $event"
        />
        <span v-if="isRatio" class="ratio-unit" data-cy="write-ratio-unit">
          %
        </span>
      </span>

      <Dropdown
        v-show="model.length > 0 && !props.showStepper"
        v-model="selectedUnit"
        optionLabel="label"
        :options="model"
        :disabled="model.length === 1 || dropdownDisabled"
        scrollHeight="400px"
        append-to="body"
        :tabindex="isDropdownSelectable ? '' : tabIndexNum"
        data-cy="write-dropdown"
        @change="onSelectedUnitChanged"
      />
      <div v-if="showStepper" class="stepper">
        <Button
          icon="far fa-angle-up"
          class="p-button-rounded p-button-secondary button-xs"
          :disabled="disableStepUp"
          data-cy="write-stepper-up"
          @click="stepper('+')"
        />
        <Button
          icon="far fa-angle-down"
          class="p-button-rounded p-button-secondary button-xs"
          :disabled="disableStepDown"
          data-cy="write-stepper-down"
          @click="stepper('-')"
        />
      </div>
    </div>

    <div
      v-else-if="mode === 'convert'"
      class="common convert-mode"
      data-cy="convert-mode"
    >
      <label
        v-if="!props.disableLabel"
        :for="key.toString()"
        class="body-4 white-space-nowrap"
        data-cy="convert-label"
      >
        {{ label }}
      </label>
      <span :id="key.toString()" class="value" data-cy="convert-value">
        {{ localizedValueWithCommas }}
        <span
          v-if="model.length === 1"
          class="ml-1 body-1"
          data-cy="convert-unit"
        >
          {{ suffix }}
        </span>
      </span>
      <Dropdown
        v-if="model.length > 0"
        v-model="selectedUnit"
        :disabled="model.length === 1 || dropdownDisabled"
        :style="`min-width: ${Math.min(7, dropDownLongestLabel)}em;`"
        class="p-inputtext-sm"
        optionLabel="label"
        scrollHeight="400px"
        append-to="body"
        :options="model"
        :tabindex="tabIndexNum"
        data-cy="convert-dropdown"
        @change="onSelectedUnitChanged"
      />
    </div>

    <div v-else class="common read-mode">
      <label
        v-if="!props.disableLabel"
        :for="key.toString()"
        class="body-4 white-space-nowrap"
      >
        {{ label }}
      </label>

      <span class="value">
        {{ localizedValueWithCommas }}
        <span style="font-size: xx-small">{{ `${flagValue ? "*" : ""}` }}</span>
        <span v-if="!disableSuffix" class="body-1" data-cy="read-unit">
          {{ suffix }}
        </span>
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ConvertableUnit } from "@/libs/units";
import { convert, ToUnit } from "@/libs/units";
import type { MaybeRef } from "@vueuse/shared";
import type { ComputedRef } from "vue";
import {
  unref,
  ref,
  computed,
  nextTick,
  watchEffect,
  watch,
  inject
} from "vue";
import { decimalFormatter } from "@/utils/utils";
import { localizedStringToNumber } from "@/utils/utils-dependent";
import {
  scenarioFormKey,
  scenarioKey,
  scenarioValidatorKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import type { ValidationTestName } from "@/composables/validation/validationTestName";
import type { Scenario } from "@/composables/forms/formTypes";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { LargeLengthUnits, SmallLengthUnits } from "@/libs/units/length";
import { GasFlowRateUnits, LiquidFlowRateUnits } from "@/libs/units/flowRate";
import { ProductivityIndexUnits } from "@/libs/units/productivityIndex";
import { PressureUnits } from "@/libs/units/pressure";
import { TemperatureUnits } from "@/libs/units/temperature";
import { API, DensityUnits } from "@/libs/units/density";
import { CurrentUnits } from "@/libs/units/current";
import { SpeedUnits } from "@/libs/units/speed";
import { VoltageUnits } from "@/libs/units/voltage";
import { ViscosityUnits } from "@/libs/units/viscosity";
import { LiquidRatioUnits } from "@/libs/units/liquidRatio";
import { MassUnits } from "@/libs/units/mass";
import { PowerUnits } from "@/libs/units/power";
import { ApparentPowerUnits } from "@/libs/units/apparentPower";
import { ConcentrationUnits } from "@/libs/units/concentration";

import { useSettingsStore } from "@/store";

export type DisplayUnitOverrides = { [key: string]: string };

const NULL_SIGIL = "";

const props = withDefaults(
  defineProps<{
    baseUnit: ConvertableUnit;
    model?: ConvertableUnit[];
    label?: string;
    unitOverrideKey?: string;
    mode: "read" | "write" | "convert";
    targetUnit?: MaybeRef<ConvertableUnit | null>;
    scenario?: Scenario | null;
    modelValue?: number | null;
    xrefId?: string | null;
    disabled?: boolean;
    hidden?: boolean;
    dropdownDisabled?: boolean;
    decimalPlaces?: number;
    max?: number;
    min?: number;
    step?: number;
    condensed?: boolean;
    placeholder?: string;
    customSuffix?: string;
    disableSuffix?: boolean;
    disableLabel?: boolean;
    useLongLabel?: boolean;
    forceHorizontal?: boolean;
    isDropdownSelectable?: boolean;
    showStepper?: boolean;
    isRatio?: boolean;
    ruleNames?: ValidationTestName[];
    scenarioValidation?: boolean;
    updateUnit?: boolean;
    updateScenario?: boolean;
    nullable?: boolean;
    flagValue?: boolean;
    debugMe?: boolean; // this is used to debug pendingValue inside of updateValue function
  }>(),
  {
    targetUnit: null,
    model: () => [],
    mode: "write",
    disabled: false,
    hidden: false,
    dropdownDisabled: false,
    decimalPlaces: 2,
    modelValue: 0,
    max: Infinity,
    min: -Infinity,
    step: 1,
    condensed: false,
    placeholder: "",
    disableSuffix: false,
    disableLabel: false,
    isDropdownSelectable: false,
    showStepper: false,
    isRatio: false,
    ruleNames: () => [] as ValidationTestName[],
    xrefId: null,
    scenarioValidation: false,
    scenario: null,
    updateUnit: true,
    updateScenario: true,
    nullable: false,
    flagValueCharacter: false,
    debugMe: false
  }
);

const emit = defineEmits<{
  (e: "update:model-value", value: number | null): void;
  (e: "update:unit-selected", value: ConvertableUnit): void;
}>();

const converterEl = ref<HTMLDivElement>();
const key = ref(Math.ceil(Math.random() * 10000));
const isFocused = ref(false);
const baseUnit = ref(props.baseUnit);
const pendingValue = ref("");
const isDirty = ref(false);

const scenarioForm = inject(scenarioFormKey, null);

const scenario = props.scenario
  ? ref(props.scenario)
  : inject(scenarioKey, ref(null));

const sizing = inject(sizingKey, null);

const overrideKey = computed<string>(
  () =>
    props.unitOverrideKey ??
    `${props.label}.${props.mode}.${props.baseUnit.label}`
);

const currentOverride = computed(
  () =>
    scenarioForm?.data.value?.display_unit_overrides?.[overrideKey.value] ??
    scenario?.value?.display_unit_overrides?.[overrideKey.value]
);

const targetUnit = computed(() => unref(props.targetUnit));

const defaultUnit = getDefaultUnitFromBaseUnit();
// stores the currently selected unit as a ref
// DO NOT compute this as it needs to be updated via the DOM
const selectedUnit = ref<ConvertableUnit>(
  targetUnit.value || defaultUnit?.value || props.baseUnit
);

watch(defaultUnit, val => (selectedUnit.value = val));

watch(targetUnit, () => {
  if (targetUnit.value) {
    nextTick(() => {
      selectedUnit.value = targetUnit.value as ConvertableUnit;
    });
  }
});

watch(currentOverride, (unitStr: string) => {
  if (unitStr) {
    selectedUnit.value =
      ToUnit(unitStr) ||
      targetUnit.value ||
      defaultUnit?.value ||
      props.baseUnit;
  }
});

const onSelectedUnitChanged = (e: Event | { value: ConvertableUnit }) => {
  // Don't write to scenarioForm if user changing units on perf evals
  if ("value" in e && props.updateUnit) {
    emit("update:unit-selected", e.value);
    if (scenarioForm && props.updateScenario) {
      scenarioForm.update({
        display_unit_overrides: {
          ...scenarioForm.data.value.display_unit_overrides,
          [overrideKey.value]: e.value.label
        }
      });
    }
  }
};

const convertedValue = computed<number | null>(() =>
  props.modelValue === null && props.nullable
    ? null
    : convert(props.modelValue, baseUnit.value, selectedUnit.value)
);

const { userSettingsNumberFormat } = useSettingsStore();

const localizedValueWithCommas = computed(() =>
  decimalFormatter(
    convertedValue.value,
    true,
    props.decimalPlaces,
    0,
    userSettingsNumberFormat.value
  )
);

const localizedValueWithoutCommas = computed(() =>
  decimalFormatter(
    convertedValue.value,
    false,
    props.decimalPlaces,
    0,
    userSettingsNumberFormat.value
  )
);

function onKeydown(event: KeyboardEvent) {
  // Set the allowed characters regex pattern
  const allowedCharacters = /^[0-9.,-]$/;

  // Check if the pressed key matches the allowed characters
  if (!allowedCharacters.test(event.key) && !isSpecialKey(event)) {
    event.preventDefault();
  }
}

function isSpecialKey(event: KeyboardEvent) {
  // List of special keys that should not be blocked
  const specialKeys = [
    "Backspace",
    "Delete",
    "ArrowLeft",
    "ArrowRight",
    "ArrowUp",
    "ArrowDown",
    "Tab",
    "Enter",
    "Shift",
    "Control",
    "Alt",
    "Meta"
  ];

  return specialKeys.includes(event.key) || event.ctrlKey || event.metaKey;
}

// update pending value each time form updates
watchEffect(() => (pendingValue.value = localizedValueWithoutCommas.value));
watch(pendingValue, () => {
  isDirty.value = true;
});

function onFocusOut(): void {
  isFocused.value = false;
  updateValue();
}

function updateValue() {
  if (pendingValue.value === "" || pendingValue.value === NULL_SIGIL) {
    if (props.nullable) {
      return emit("update:model-value", null);
    } else {
      pendingValue.value = "0";
    }
  }

  if (pendingValue.value.endsWith(",") || pendingValue.value.endsWith(".")) {
    pendingValue.value = pendingValue.value.slice(0, -1);
  }

  let pendingValueNum = localizedStringToNumber(
    pendingValue.value,
    userSettingsNumberFormat.value
  );

  // Number is unformattable, reset to inital value
  if (isNaN(pendingValueNum)) {
    pendingValue.value = localizedValueWithoutCommas.value;
    return;
  }

  const convertedPendingValueNum = baseUnit.value.toThisUnit(
    selectedUnit.value.toStandardUnit(pendingValueNum)
  );

  // check against min/max converted prop
  pendingValueNum = Math.min(props.max, convertedPendingValueNum);
  pendingValueNum = Math.max(props.min, pendingValueNum);

  if (props.debugMe) {
    console.log("isDirty", isDirty.value, "pending", pendingValueNum);
  }
  if (isDirty.value === true) {
    emit("update:model-value", pendingValueNum);
    isDirty.value = false;
  }
}

const dropDownLongestLabel = computed(() =>
  props.model.reduce(
    (acc, u) => (u.label.length > acc ? u.label.length : acc),
    0
  )
);

function onFocusIn(e: FocusEvent): void {
  isFocused.value = true;

  const input = e.target as HTMLInputElement;
  nextTick(() => input.select());
}

const disableStepUp = computed(
  () => (!!props.modelValue && props.modelValue >= props.max) || props.disabled
);
const disableStepDown = computed(
  () => (!!props.modelValue && props.modelValue <= props.min) || props.disabled
);

function stepper(direction: "+" | "-"): void {
  const convert = (n: number) =>
    selectedUnit.value.toThisUnit(baseUnit.value.toStandardUnit(n));

  const newVal =
    Number(pendingValue.value) +
    (direction === "+" ? convert(props.step) : -convert(props.step));

  pendingValue.value = newVal.toString();
  isDirty.value = true;
  updateValue();
}

const scenarioValidator = inject(scenarioValidatorKey, null);
const errors = computed(
  () =>
    scenarioValidator?.filterByXrefIdAndRuleNames(props.ruleNames, props.xrefId)
      .value || []
);

const severityLevels = ["success", "info", "warn", "error", "catastrophe"];

const severityClass = computed(() =>
  props.disabled
    ? ""
    : severityLevels[Math.max(...unref(errors).map(err => err.severity))]
);

const tooltipData = computed(() => ({
  value:
    unref(errors)[0]?.message +
    (unref(errors).length > 1
      ? ` and ${unref(errors).length - 1} more...`
      : ""),
  disabled: props.disabled || !unref(errors)[0],
  class: severityClass.value
}));

const tabIndexNum = -1;

// Looks at base unit from prop
// Finds which unit type it belongs to based on unit label
// Runs get*Unit function from useScenarioUnits
// and returns the default unit for that unit type
function getDefaultUnitFromBaseUnit(): ComputedRef<ConvertableUnit> {
  // API gravity only follows an explicit user selection, never the generic density preset
  if (props.baseUnit.label === API.label) {
    return computed(() => ToUnit(currentOverride.value) || props.baseUnit);
  }
  const u = useScenarioUnits(scenario, sizing);
  const unitListUnitDefaultFnPairs: [
    ConvertableUnit[],
    (overrideKey?: string) => ComputedRef<ConvertableUnit>
  ][] = [
    [LargeLengthUnits.options(), u.getLargeLengthUnit],
    [SmallLengthUnits.options(), u.getSmallLengthUnit],
    [LiquidFlowRateUnits.options(), u.getLiquidFlowUnit],
    [GasFlowRateUnits.options(), u.getGasFlowUnit],
    [ProductivityIndexUnits.options(), u.getProductivityFlowUnit],
    [PressureUnits.options(), u.getPressureUnit],
    [TemperatureUnits.options(), u.getTempUnit],
    [DensityUnits.options(), u.getDensityUnit],
    [CurrentUnits.options(), u.getCurrentUnit],
    [SpeedUnits.options(), u.getSpeedUnit],
    [VoltageUnits.options(), u.getVoltageUnit],
    [ViscosityUnits.options(), u.getViscosityUnit],
    [LiquidRatioUnits.options(), u.getLiquidRatioUnit],
    [MassUnits.options(), u.getMassUnit],
    [PowerUnits.options(), u.getHydraulicPowerUnit],
    [PowerUnits.options(), u.getRealPowerUnit],
    [ApparentPowerUnits.options(), u.getApparentPowerUnit],
    [ConcentrationUnits.options(), u.getConcentrationUnit]
  ];

  for (const [units, getUnitFn] of unitListUnitDefaultFnPairs) {
    const unitLabels = units.map(unit => unit.label);
    if (unitLabels.includes(props.baseUnit.label)) {
      return getUnitFn(overrideKey.value);
    }
  }

  return computed(() => props.baseUnit);
}

const suffix = computed(() => {
  if (props.customSuffix) {
    return props.customSuffix;
  }
  if (props.useLongLabel) {
    return selectedUnit.value.label ?? selectedUnit.value.shortLabel ?? "";
  }
  return selectedUnit.value.shortLabel ?? selectedUnit.value.label ?? "";
});
</script>

<style lang="scss">
// Convert code goes here for now - will move when rehaul after Primevue moves away from SCSS - @MM - 12-11-23
.converter {
  &.horizontal-input {
    .common {
      .value {
        display: flex !important;
        flex-direction: row !important;
        gap: 0.2em;
        align-items: baseline;
      }
    }
  }
  &.converter-stepper {
    position: relative;
  }

  &.condensed {
    .common {
      label {
        color: var(--gray-800);
        font-size: 12px;
      }

      .value {
        color: var(--gray-900);
        font-weight: 700;
        font-size: 14px;
      }
    }
  }

  .common {
    padding: 0.325em;
    display: grid;
    gap: 0.25em;
    align-items: center;
    grid-template-columns: minmax(6em, 1fr) minmax(5em, min-content);
    border: 1px solid transparent;
    border-radius: 4px;

    label {
      font-size: 12px;
      font-weight: 400;
      color: var(--gray-700);
      grid-column: 1/-1;
      grid-row: 1;
    }

    .value {
      color: var(--gray-900);
      font-size: 18px;
      font-weight: 500;
      font-family: var(--monospace-family);
    }

    &:nth-child(1) {
      grid-column: 1/-1;
      grid-row: 1;
    }

    &:nth-child(2) {
      grid-column: 1;
      grid-row: 2;
    }

    &:nth-child(3) {
      grid-column: 2;
      grid-row: 2;
    }

    .p-dropdown {
      grid-column: 2;
      grid-row: 2;
      border: 0;
      box-shadow: var(--shadow-main);
      padding: 0;
      align-self: flex-start;

      > .p-dropdown-label {
        font-size: 12px;
        line-height: 1;
        padding: 0.5em;
      }
    }
  }

  .write-mode {
    border: 1px solid var(--border-main);
    border-radius: 4px;
    box-shadow: var(--shadow-main);
    background: var(--surface-25);
    transition: all 0.2s ease-out;

    &.warn {
      border: 1px solid var(--warning-500);
    }

    &.error,
    &.catastrophe {
      border: 1px solid var(--danger-500);
    }

    label.warn {
      color: var(--warning-500);
    }

    label.error {
      color: var(--danger-500);
    }

    &:focus-within {
      background: var(--surface-0);
      border-color: var(--info-500);
      box-shadow: var(--shadow-info) !important;
    }

    &:hover:not(:focus-within):not(.disabled) {
      background-color: var(--surface-hover);
    }

    .p-inputnumber {
      grid-column: 1;
      grid-row: 2;
      min-width: 0;

      > .p-inputtext {
        min-width: 0px;
        border: 0 !important;
        background: transparent;
        appearance: none;
        padding: 0 !important;
        font-weight: 500;
        font-size: 18px;
        font-family: var(--monospace-family);

        &:focus {
          box-shadow: none;
        }

        &::selection {
          background: var(--gray-500);
        }
      }
    }

    &.ratio {
      position: relative;

      .p-inputtext {
        max-width: 40px;
      }

      .ratio-unit {
        color: var(--gray-900);
        font-size: 18px;
        font-weight: 500;
        font-family: var(--monospace-family);
      }
    }

    .stepper {
      padding: 0;
      position: absolute;
      width: 1.75em;
      top: 0px;
      bottom: 0px;
      right: 0px;
      height: 100%;
      border-color: var(--border-main);
      border-width: 0 0 0 1px;
      border-style: solid;
      background-color: var(--gray-300);
      border-radius: 0 4px 4px 0;
      text-align: center;

      button.p-button {
        box-shadow: none !important;
        font-size: 11px;
        width: 1rem;
        height: 1rem;
        padding: 0rem;

        &:first-child {
          margin-top: 8px;
          margin-bottom: 4px;
        }
      }
    }
  }
  .p-inputnumber .p-inputtext {
    width: 100%;

    &::placeholder {
      opacity: 0.4;
    }
  }
}

// Change needed here for converter in mobile view

@media only screen and (max-width: 750px) {
  .converter .common {
    padding: 0;
  }
  .converter .write-mode .stepper {
    width: auto;
  }
  .mobile-wrap {
    display: flex !important;
    flex-wrap: wrap;
  }
}
</style>
